import { FC, useState } from 'react';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import {
  ArrowSquareOutIcon,
  CheckCircleIcon,
  WarningIcon,
} from '@phosphor-icons/react';

import type { DomainProfileFieldNeeds } from './domainProfileFieldNeeds';

/** Show at most this many missing fields before collapsing behind "show more". */
const COLLAPSED_FIELD_COUNT = 3;

interface DomainFieldNeedsBannerProps {
  needs: DomainProfileFieldNeeds;
  onGoToHub: () => void;
}

/**
 * Per-domain nudge shown on the assessment workflow step: lists the
 * Organization Profile fields this domain consumes that are still missing, or
 * confirms the profile is complete for the domain. Renders nothing when the
 * domain consumes no profile fields.
 */
export const DomainFieldNeedsBanner: FC<DomainFieldNeedsBannerProps> = observer(({
  needs,
  onGoToHub,
}) => {
  const theme = useTheme();
  const { t } = useTrans();
  const [expanded, setExpanded] = useState(false);

  if (needs.totalFields === 0) return null;

  if (needs.complete) {
    const accent = theme.palette.success.main;
    return (
      <Paper
        variant="panel"
        elevation={0}
        sx={{
          mb: 2,
          py: 1.25,
          px: 1.75,
          backgroundColor: alpha(accent, 0.1),
          border: `1px solid ${alpha(accent, 0.4)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon size={18} color={accent} weight="fill" />
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {t('app.orgProfile.nudge.domain.completeTitle')}
          </Typography>
        </Box>
      </Paper>
    );
  }

  const accent = theme.palette.warning.main;
  const missing = needs.fields.filter((field) => !field.filled);
  const isTruncated = missing.length > COLLAPSED_FIELD_COUNT;
  const visibleMissing =
    expanded || !isTruncated ? missing : missing.slice(0, COLLAPSED_FIELD_COUNT);
  const hiddenCount = missing.length - visibleMissing.length;

  return (
    <Paper
      variant="panel"
      elevation={0}
      sx={{
        mb: 2,
        py: 1.25,
        px: 1.75,
        backgroundColor: alpha(accent, 0.1),
        border: `1px solid ${alpha(accent, 0.4)}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <WarningIcon size={18} color={accent} weight="fill" />
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {t('app.orgProfile.nudge.domain.missingTitle', {
            missing: missing.length,
          })}
        </Typography>
      </Box>

      <Box component="ul" sx={{ m: 0, mb: 1, pl: 3 }}>
        {visibleMissing.map((field) => (
          <Typography
            key={field.fieldId}
            component="li"
            variant="caption"
            sx={{ color: 'text.secondary' }}
          >
            {field.label}
          </Typography>
        ))}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {isTruncated && (
          <Button
            variant="link"
            onClick={() => setExpanded((prev) => !prev)}
            sx={{ p: 0, minWidth: 0 }}
          >
            {expanded
              ? t('app.orgProfile.nudge.domain.showLess')
              : t('app.orgProfile.nudge.domain.showMore', { hidden: hiddenCount })}
          </Button>
        )}
        <Button
          variant="link"
          onClick={onGoToHub}
          startIcon={<ArrowSquareOutIcon size={14} />}
          sx={{ p: 0, minWidth: 0 }}
        >
          {t('app.orgProfile.nudge.domain.cta')}
        </Button>
      </Box>
    </Paper>
  );
});
